import { useState, useEffect, useRef } from "react";
import {
	Elements,
	CardElement,
	useStripe,
	useElements,
} from "@stripe/react-stripe-js";

import { API_URL, translate, fmtCurrency } from "../../utils";
import useFetch from "../../utils/useFetch";

import Alert from "../../components/Alert";
import Input from "../../components/Input";
import Select from "../../components/Select";
import Checkboxes from "../../components/Checkboxes";
import Radios from "../../components/Radios";
import InputStripe from "../../components/InputStripe";
import Terms from "../../components/Terms";
import Msg from "../../components/Msg";
import Submit from "../../components/Submit";

const fmtEvent = (eventId, lang) => {
	const event =
		eventId === "clasificatorio-mexicano" ? "Clasificatorio Mexicano" : "";

	return translate(
		lang,
		`Cuota de Inscripción - ${event}`,
		`Cuota de Inscripción - ${event}`,
	);
};

const Home = ({ status, lang }) => {
	const [url, setUrl] = useState(null);
	const [errors, setErrors] = useState([]);
	const [names, setNames] = useState({
		events: [],
	});
	const [msg, setMsg] = useState({});

	const formElement = useRef(null);
	const stripe = useStripe();
	const elements = useElements();
	const { loading, data } = useFetch(url);

	useEffect(() => {
		const newUrl = new URL(`${API_URL}/cmHomeInit`);
		const params = {
			lang,
		};
		newUrl.search = new URLSearchParams(params);
		setUrl(newUrl);
	}, []);

	useEffect(() => {
		if (data && data.resp === 1) {
			setNames({
				...data,
			});
		}
	}, [data]);

	const handleUpdate = (name, value) => {
		setNames((names) => ({
			...names,
			[name]: value,
		}));
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setErrors([]);
		setMsg({
			type: "working",
			text: "",
		});

		const url = new URL(`${API_URL}/cmHome`);
		let data = new FormData();
		Object.entries(names).forEach(([key, value]) => {
			if (key === "nationality") {
				data.append(key, value ? "1" : "0");
			} else {
				data.append(key, value);
			}
		});
		data.append("event", "clasificatorio-mexicano");
		data.append("lang", lang);

		try {
			const response = await fetch(url, {
				method: "POST",
				body: data,
			});
			const json = await response.json();
			if (json && json.resp === 1) {
				handlePayment(json.clientSecret);
			} else {
				setErrors(json.fields);
				setMsg({
					type: "error",
					text: json.text,
				});
			}
		} catch (error) {
			setMsg({
				type: "error",
				text: "An error has occurred.",
			});
		}
	};

	const handlePayment = async (clientSecret) => {
		const result = await stripe.confirmCardPayment(clientSecret, {
			payment_method: {
				card: elements.getElement(CardElement),
				billing_details: {
					name: `${names.fname} ${names.lname}`,
					email: `${names.email}`,
				},
			},
		});

		if (result.error) {
			setMsg({
				type: "error",
				text: result.error.message,
			});
		} else {
			if (result.paymentIntent.status === "succeeded") {
				handleSuccess({
					id: result.paymentIntent.id,
					status: result.paymentIntent.status,
					paymentMethod: result.paymentIntent.payment_method,
					amount: result.paymentIntent.amount,
					currency: result.paymentIntent.currency,
				});
			}
		}
	};

	const handleSuccess = async (stripeResponse) => {
		const url = new URL(`${API_URL}/cmSubmit`);
		let data = new FormData();
		Object.entries(names).forEach(([key, value]) => data.append(key, value));
		data.append("stripeResponse", JSON.stringify(stripeResponse));
		data.append("event", "clasificatorio-mexicano");
		data.append("lang", lang);

		try {
			const response = await fetch(url, {
				method: "POST",
				body: data,
			});
			const json = await response.json();
			if (json && json.resp === 1) {
				status(json.id, 1);
			} else {
				setErrors(json.fields);
				setMsg({
					type: "error",
					text: json.text,
				});
			}
		} catch (error) {
			setMsg({
				type: "error",
				text: "An error has occurred.",
			});
		}
	};

	return (
		<>
			{loading ? (
				<Alert
					data={{
						type: "working",
						text: "",
					}}
				/>
			) : (
				<>
					<div className="match-play-container">
						<div>
							<div className="subhead">
								{translate(
									lang,
									`Gracias por tu interés en jugar el Clasificatorio Mexicano presentado por National Car Rental. Por favor, completa la siguiente información para completar tu participación y procesar el pago de la inscripción.`,
									`Gracias por tu interés en jugar el Clasificatorio Mexicano presentado por National Car Rental. Por favor, completa la siguiente información para completar tu participación y procesar el pago de la inscripción.`,
								)}
							</div>
							<div className="spacer-15"> </div>
						</div>
					</div>
					<form
						ref={formElement}
						method="post"
						action="/"
						onSubmit={(e) => handleSubmit(e)}
					>
						<table>
							<thead>
								<tr>
									<th> {translate(lang, `Cantidad`, `Cantidad`)} </th>
									<th className="left">
										{translate(lang, `Descripción`, `Descripción`)}
									</th>
									<th> {translate(lang, `Precio`, `Precio`)} </th>
									<th> {translate(lang, `Total`, `Total`)} </th>
								</tr>
							</thead>
							<tbody>
								<tr>
									<td className="center"> 1 </td>
									<td> {fmtEvent("clasificatorio-mexicano", lang)} </td>
									<td className="right"> {fmtCurrency(names.amt / 100)} </td>
									<td className="right"> {fmtCurrency(names.amt / 100)} </td>
								</tr>
							</tbody>
							<tfoot>
								<tr>
									<th colSpan="3" className="right">
										{translate(lang, `TOTAL`, `TOTAL`)}
									</th>
									<th className="right"> {fmtCurrency(names.amt / 100)} </th>
								</tr>
							</tfoot>
						</table>

						<h3>
							{translate(
								lang,
								"Información del Participante",
								"Información del Participante",
							)}
						</h3>
						<div className="form-group">
							<Input
								name="fname"
								label={translate(lang, "Nombre(s)", "Nombre(s)")}
								reqd={true}
								value={names.fname || ""}
								update={handleUpdate}
								errors={errors}
								autocomplete="given-name"
							/>

							<Input
								name="lname"
								label={translate(lang, "Apellidos", "Apellidos")}
								reqd={true}
								value={names.lname || ""}
								update={handleUpdate}
								errors={errors}
								autocomplete="family-name"
							/>
						</div>
						<div className="form-group">
							<Input
								name="email"
								label={translate(
									lang,
									"Correo Electrónico",
									"Correo Electrónico",
								)}
								reqd={true}
								value={names.email || ""}
								update={handleUpdate}
								errors={errors}
							/>
							<div className="two-inputs">
								<Select
									name="phoneCode"
									label={translate(lang, "Código del País", "Código del País")}
									reqd={true}
									value={names.phoneCode || ""}
									options={[
										{
											id: "+1",
											name: "US / CA (+1)",
										},
										{
											id: "+52",
											name: "MX (+52)",
										},
										{
											id: "other",
											name: `${translate(lang, "Otros", "Otros")}`,
										},
									]}
									update={handleUpdate}
									errors={errors}
									autocomplete="tel-country-code"
								/>
								<Input
									name="phone"
									label={translate(
										lang,
										"Teléfono Celular",
										"Teléfono Celular",
									)}
									reqd={true}
									value={names.phone || ""}
									update={handleUpdate}
									errors={errors}
									autocomplete="tel-national"
								/>
							</div>
						</div>
						<div className="form-group">
							<div className="two-inputs-flip">
								<Input
									name="hometown"
									label={translate(
										lang,
										"Ciudad de Origen",
										"Ciudad de Origen",
									)}
									reqd={true}
									value={names.hometown || ""}
									update={handleUpdate}
									errors={errors}
									autocomplete="off"
								/>
								<Select
									name="state"
									label={translate(lang, "Estado", "Estado")}
									reqd={true}
									value={names.state || ""}
									options={[
										{
											id: "",
											name: "--",
										},
									].concat(
										[
											"Ags.",
											"B.C.",
											"B.C.S.",
											"Camp.",
											"Chis.",
											"Chih.",
											"Coah.",
											"Col.",
											"CDMX",
											"Dgo.",
											"Gto.",
											"Gro.",
											"Hgo.",
											"Jal.",
											"Méx.",
											"Mich.",
											"Mor.",
											"Nay.",
											"N.L.",
											"Oax.",
											"Pue.",
											"Qro.",
											"Q. Roo.",
											"S.L.P.",
											"Sin.",
											"Son.",
											"Tab.",
											"Tamps.",
											"Tlax.",
											"Ver.",
											"Yuc.",
											"Zac.",
										].map((item) => {
											return {
												id: item,
												name: item,
											};
										}),
									)}
									update={handleUpdate}
									errors={errors}
									autocomplete="address-level1"
								/>
							</div>

							<Input
								name="homeCourse"
								label={translate(lang, "Club de Golf", "Club de Golf")}
								reqd={true}
								value={names.homeCourse || ""}
								update={handleUpdate}
								errors={errors}
								autocomplete="off"
							/>
						</div>
						<div className="form-group radios inline">
							<Checkboxes
								label=""
								reqd={false}
								data={[
									{
										name: "nationality",
										val: "",
										label: `${translate(
											lang,
											"Favor de seleccionar aquí para confirmar tu nacionalidad mexicana y que estás de acuerdo que solo jugadores mexicanos son elegibles para participar en el Clasificatorio Mexicano presentado por National Car Rental.  Al seleccionar aquí, confirmas estar de acuerdo que los organizadores podrían pedir que presentes identificación oficial para comprobar tu nacionalidad.  En caso de que algún jugador no pueda comprobar su nacionalidad mexicana, será descalificado y no habrá devolución de su cuota de inscripción.",
											"Favor de seleccionar aquí para confirmar tu nacionalidad mexicana y que estás de acuerdo que solo jugadores mexicanos son elegibles para participar en el Clasificatorio Mexicano presentado por National Car Rental.  Al seleccionar aquí, confirmas estar de acuerdo que los organizadores podrían pedir que presentes identificación oficial para comprobar tu nacionalidad.  En caso de que algún jugador no pueda comprobar su nacionalidad mexicana, será descalificado y no habrá devolución de su cuota de inscripción.",
										)}`,
									},
								]}
								values={[names.nationality || false]}
								click={handleUpdate}
							/>
						</div>
						<div className="form-group radios inline">
							<Radios
								label={translate(
									lang,
									"¿Eres profesional o amateur?",
									"¿Eres profesional o amateur?",
								)}
								reqd={true}
								data={[
									{
										name: "pro",
										val: "pro",
										label: `${translate(lang, "Profesional", "Profesional")}`,
									},
									{
										name: "pro",
										val: "amateur",
										label: `${translate(lang, "Amateur", "Amateur")}`,
									},
								]}
								value={names.pro || ""}
								click={handleUpdate}
							/>
						</div>
						{names.pro === "pro" && (
							<div className="form-group">
								<Select
									name="tour"
									label={translate(
										lang,
										"¿A cual gira perteneces como miembro?",
										"¿A cual gira perteneces como miembro?",
									)}
									reqd={true}
									value={names.tour || ""}
									options={[
										{
											id: "",
											name: "--",
										},
									].concat(
										[
											"PGA TOUR",
											"Korn Ferry Tour",
											"PGA TOUR Americas",
											"DP World Tour",
											"Challenge Tour",
											"Gira de Golf MX",
											"Mini-tours",
											"Profesional de club",
											"Otro",
										].map((item) => {
											return {
												id: item,
												name: item,
											};
										}),
									)}
									update={handleUpdate}
									errors={errors}
									autocomplete="off"
								/>

								<div className="empty" />
							</div>
						)}
						{names.pro === "amateur" && (
							<p
								style={{
									marginBottom: "10px",
									lineHeight: "18px",
								}}
							>
								{translate(
									lang,
									"Jugadores amateurs deben tener un handicap de 2 o mejor para poder participar. Al no inscribirse con su número de GHIN, se te solicitará una comprobación oficial de tu Hándicap. El comité organizador se reserva el derecho de revisar cualquier documentación de Hándicap y se reserva el derecho de verificar la elegibilidad de algún jugador en su propio juicio y completa determinación.  En caso de que algún jugador no pueda comprobar su Hándicap, será descalificado y no habrá devolución de su cuota de inscripción.",
									"Jugadores amateurs deben tener un handicap de 2 o mejor para poder participar. Al no inscribirse con su número de GHIN, se te solicitará una comprobación oficial de tu Hándicap. El comité organizador se reserva el derecho de revisar cualquier documentación de Hándicap y se reserva el derecho de verificar la elegibilidad de algún jugador en su propio juicio y completa determinación.  En caso de que algún jugador no pueda comprobar su Hándicap, será descalificado y no habrá devolución de su cuota de inscripción.",
								)}
							</p>
						)}
						{names.pro === "amateur" && (
							<div className="form-group">
								<Input
									name="handicap"
									label={translate(lang, "Hándicap", "Hándicap")}
									reqd={true}
									value={names.handicap || ""}
									update={handleUpdate}
									errors={errors}
									autocomplete="off"
								/>

								<Input
									name="ghin"
									label={translate(lang, "Número de GHIN", "Número de GHIN")}
									reqd={false}
									value={names.ghin || ""}
									update={handleUpdate}
									errors={errors}
									autocomplete="off"
								/>
							</div>
						)}
						<h3
							style={{
								marginTop: "12.5px",
							}}
						>
							{translate(lang, "Payment", "Pago")}
						</h3>
						<div className="form-group one">
							<InputStripe
								name="card"
								label={translate(
									lang,
									"Tarjeta de Débito o Crédito",
									"Tarjeta de Débito o Crédito",
								)}
								reqd={true}
							/>
						</div>
						<Terms
							hdg={translate(
								lang,
								"Verifica los detalles y haz click en procesar pago para confirmar tu inscripción.",
								"Verifica los detalles y haz click en procesar pago para confirmar tu inscripción.",
							)}
							text={translate(
								lang,
								`Todos los cargos a tarjeta de débito o crédito se verán reflejados como “WWT CHAMPIONSHIP” en tu estado de cuenta. Al hacer click en el botón de procesar pago, confirmarás tu participación y se hará un cargo a tu tarjeta de ${fmtCurrency(
									names.amt / 100,
								)}.  Todas las ventas son finales.  En caso de hacer WD antes de la fecha límite del 2 de octubre, se te cobrará un cargo de cancelación de $800 MXN.  En caso de hacer WD después de la fecha límite, no hay reembolso.  Al ingresar la información del pago, aceptaste las condiciones de compra.`,
								`Todos los cargos a tarjeta de débito o crédito se verán reflejados como “WWT CHAMPIONSHIP” en tu estado de cuenta. Al hacer click en el botón de procesar pago, confirmarás tu participación y se hará un cargo a tu tarjeta de ${fmtCurrency(
									names.amt / 100,
								)}.  Todas las ventas son finales.  En caso de hacer WD antes de la fecha límite del 2 de octubre, se te cobrará un cargo de cancelación de $800 MXN.  En caso de hacer WD después de la fecha límite, no hay reembolso.  Al ingresar la información del pago, aceptaste las condiciones de compra.`,
							)}
						/>
						<div className="msg-submit">
							{msg.type && <Msg data={msg} />}
							<div
								className={
									msg.type === "working" || msg.type === "success"
										? "hidden"
										: "submit-container"
								}
							>
								<Submit
									path=""
									left=""
									submit={translate(lang, "Procesar Pago", "Procesar Pago")}
								/>
							</div>
						</div>
					</form>
				</>
			)}
		</>
	);
};

const Payment = ({ status, stripePromise, lang }) => {
	return (
		<Elements stripe={stripePromise}>
			<Home status={status} lang={lang} />
		</Elements>
	);
};

export default Payment;
